import { useState } from "react";
import SharedAddressNotice from "@/components/SharedAddressNotice";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { AlertTriangle, Ban } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import type {
  EmailDetail,
  MessageView,
  ProspectDetail,
  ThreadSummary,
  TransitionView,
} from "@/lib/types";
import { Card, CardTitle } from "@/components/Card";
import StatusBadge from "@/components/StatusBadge";
import ProspectFlowCard from "@/components/ProspectFlowCard";
import ProspectConversation from "@/components/ProspectConversation";
import QualificationCard from "@/components/QualificationCard";
import SandboxProspectTools from "@/components/SandboxProspectTools";
import { ApprovalModeBadge, FlowStateBadge } from "@/components/EmailStatusBadge";
import { formatDateTime } from "@/lib/format";

/**
 * How often this page re-reads the conversation.
 *
 * <p>Everything on it moves without the operator touching anything: the agent
 * ticks on a schedule, replies arrive by IMAP, and a sandboxed rehearsal
 * composes the other side of the conversation from a job queue seconds after a
 * send. A page that only fetched on mount would show a conversation that had
 * already moved on, and the operator would have no way of knowing. Exported so
 * the test that proves it can wait exactly one interval rather than guess.
 */
export const PROSPECT_REFRESH_MS = 10_000;

/**
 * One prospect, arranged around the only two questions this page is opened to
 * answer: what did they say, and what are we about to send back.
 *
 * <p>The conversation is the page — opened, newest first, with the message
 * awaiting approval already in its reviewer. The facts that qualify what is
 * read there (phase, strategy, when the agent next acts) sit above it; the
 * controls and the slower-moving record sit beside it.
 */
export default function ProspectDetailPage() {
  const { id } = useParams<{ id: string }>();

  const detail = useQuery({
    queryKey: ["prospect", id],
    queryFn: () => api.get<ProspectDetail>(`/api/v1/prospects/${id}`),
    enabled: !!id,
    refetchInterval: PROSPECT_REFRESH_MS,
  });
  const threads = useQuery({
    queryKey: ["prospect-threads", id],
    queryFn: () => api.get<ThreadSummary[]>(`/api/v1/prospects/${id}/threads`),
    enabled: !!id,
    refetchInterval: PROSPECT_REFRESH_MS,
  });
  const transitions = useQuery({
    queryKey: ["prospect-transitions", id],
    queryFn: () => api.get<TransitionView[]>(`/api/v1/prospects/${id}/transitions`),
    enabled: !!id,
    refetchInterval: PROSPECT_REFRESH_MS,
  });

  // The blocked message, fetched here so the conversation knows which thread to
  // open before anything is clicked. Shares its cache key with the reviewer
  // that renders it, so this costs no extra request.
  const pendingDraftId = detail.data?.pendingDraftId ?? null;
  const pendingDraft = useQuery({
    queryKey: ["email", pendingDraftId],
    queryFn: () => api.get<EmailDetail>(`/api/v1/emails/${pendingDraftId}`),
    enabled: !!pendingDraftId,
  });

  if (detail.isLoading) return <div className="text-sm text-zinc-500">Loading…</div>;
  if (detail.error || !detail.data) {
    return (
      <div className="text-sm text-[var(--danger)]">
        Prospect not found. <Link className="underline" to="/prospects">Back to list</Link>
      </div>
    );
  }

  const p = detail.data;

  return (
    <div className="space-y-4">
      <div>
        <Link to="/prospects" className="text-sm text-[var(--primary)] hover:underline">
          ← Back to prospects
        </Link>
      </div>

      <ProspectHeader p={p} />

      {p.unsubscribedAt ? (
        <Notice tone="danger" icon={<Ban className="h-4 w-4 shrink-0" aria-hidden />}>
          This prospect unsubscribed on {formatDateTime(p.unsubscribedAt)}. Nothing further
          will be sent to them, by the agent or by hand.
        </Notice>
      ) : null}

      <SharedAddressNotice strategyId={p.strategyId} email={p.email} excludeId={p.id} variant="detail" />

      {pendingDraftId ? (
        <Notice tone="warn" icon={<AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />}>
          <span className="font-medium">Awaiting your approval.</span> The agent has composed
          the next message and is waiting — it is open below, above the reply it answers.
          Nothing further will be sent until you approve or reject it.
        </Notice>
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="min-w-0 space-y-4 lg:col-span-2">
          <ProspectConversation
            key={p.id}
            threads={threads.data ?? []}
            threadsLoading={threads.isLoading}
            pendingDraft={pendingDraftId ? pendingDraft.data ?? null : null}
            refreshMs={PROSPECT_REFRESH_MS}
          />
          <ManualReply
            prospectId={p.id}
            prospectEmail={p.email}
            disabled={!!p.unsubscribedAt}
          />
          <SandboxProspectTools prospectId={p.id} />
        </div>

        <div className="min-w-0 space-y-4">
          <ProspectFlowCard p={p} />
          <QualificationCard prospectId={p.id} />
          <AboutCard p={p} />
          <PhaseHistoryCard transitions={transitions.data ?? []} />
        </div>
      </div>
    </div>
  );
}

/**
 * Who this is, what state they are in, and the handful of facts that change how
 * the conversation below should be read — all above it, in that order.
 */
function ProspectHeader({ p }: { p: ProspectDetail }) {
  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">{p.fullName ?? p.email}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 text-sm text-[var(--fg-muted)]">
            <span>{p.email}</span>
            {p.phone ? <span>{p.phone}</span> : null}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={p.status} />
          <FlowStateBadge state={p.flowState} />
          <ApprovalModeBadge
            mode={p.effectiveApprovalMode}
            overridden={p.approvalMode != null}
          />
        </div>
      </div>

      <dl className="mt-4 flex flex-wrap gap-x-10 gap-y-3 border-t border-[var(--border)] pt-4">
        <Fact k="Phase" v={p.currentPhaseName ?? p.currentPhaseSlug ?? "—"} />
        <Fact k="Strategy" v={p.strategySlug} />
        <Fact k="Next action" v={formatDateTime(p.nextActionAt)} />
        <Fact k="Last activity" v={formatDateTime(p.lastActivityAt)} />
        {p.tags.length > 0 ? <Fact k="Tags" v={p.tags.join(", ")} /> : null}
      </dl>
    </Card>
  );
}

function Fact({ k, v }: { k: string; v: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs uppercase tracking-wide text-[var(--fg-muted)]">{k}</dt>
      <dd className="mt-0.5 text-sm">{v}</dd>
    </div>
  );
}

function Notice({
  tone,
  icon,
  children,
}: {
  tone: "warn" | "danger";
  icon: ReactNode;
  children: ReactNode;
}) {
  const styles =
    tone === "danger"
      ? "border-rose-300 bg-rose-50 text-rose-900"
      : "border-amber-300 bg-amber-50 text-amber-900";
  return (
    <div role="status" className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${styles}`}>
      {icon}
      <div>{children}</div>
    </div>
  );
}

/**
 * The slower-moving record: how this prospect may be contacted, and the
 * strategy-declared fields carried with them. Everything here is context for
 * the conversation, not a reason to open the page.
 */
function AboutCard({ p }: { p: ProspectDetail }) {
  const custom = Object.entries(p.customFields ?? {});
  return (
    <Card>
      <CardTitle>About</CardTitle>
      <dl className="space-y-1.5 text-sm">
        <Row k="Timezone" v={p.timezone ?? "—"} />
        <Row k="Consent given" v={formatDateTime(p.consentGivenAt)} />
        <Row k="Unsubscribed" v={formatDateTime(p.unsubscribedAt)} />
        <Row k="Added" v={formatDateTime(p.createdAt)} />
        {custom.map(([key, value]) => (
          <Row key={key} k={key.replace(/_/g, " ")} v={displayField(value)} />
        ))}
      </dl>
    </Card>
  );
}

/** Strategy-declared extra fields are whatever the prospect_schema defines. */
function displayField(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function PhaseHistoryCard({ transitions }: { transitions: TransitionView[] }) {
  return (
    <Card>
      <CardTitle>Phase history</CardTitle>
      {transitions.length > 0 ? (
        <ol className="space-y-2 text-sm">
          {transitions.map((t) => (
            <li key={t.id} className="border-l-2 border-zinc-200 pl-3">
              <div className="text-xs text-[var(--fg-muted)]">{formatDateTime(t.createdAt)}</div>
              <div>
                {t.fromPhaseName ?? "Start"} → {t.toPhaseName ?? "unknown phase"}
              </div>
              <div className="text-xs text-[var(--fg-muted)]">{t.trigger}</div>
              {t.notes ? (
                // Why a human moved them. The agent is given this same text as
                // context for the next follow-up, so it belongs in the record
                // an operator reads too.
                <div className="mt-1 whitespace-pre-wrap rounded bg-zinc-50 px-2 py-1 text-xs text-zinc-700 ring-1 ring-zinc-100">
                  {t.notes}
                </div>
              ) : null}
            </li>
          ))}
        </ol>
      ) : (
        <div className="text-sm text-[var(--fg-muted)]">No transitions yet.</div>
      )}
    </Card>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="grid grid-cols-[8rem_1fr] gap-2">
      <dt className="text-[var(--fg-muted)]">{k}</dt>
      <dd className="break-words">{v}</dd>
    </div>
  );
}

/** Write to this prospect by hand, through the same pipeline the agent uses. */
function ManualReply({
  prospectId,
  prospectEmail,
  disabled,
}: {
  prospectId: string;
  prospectEmail: string;
  disabled: boolean;
}) {
  const queryClient = useQueryClient();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const send = useMutation({
    mutationFn: (req: { subject: string; bodyText: string }) =>
      api.post<MessageView>(`/api/v1/prospects/${prospectId}/manual-reply`, req),
    onSuccess: () => {
      setSubject("");
      setBody("");
      queryClient.invalidateQueries({ queryKey: ["prospect-threads", prospectId] });
      queryClient.invalidateQueries({ queryKey: ["thread-messages"] });
    },
  });

  if (disabled) {
    return (
      <Card>
        <CardTitle>Write to this prospect</CardTitle>
        <div className="text-sm text-[var(--fg-muted)]">
          This prospect has unsubscribed — outbound is disabled.
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <CardTitle>Write to this prospect</CardTitle>
      <p className="mb-3 text-xs text-[var(--fg-muted)]">
        Sends to <code>{prospectEmail}</code> via the configured email provider.
        Routes through the same suppression + threading logic as the agent.
      </p>
      <form
        className="space-y-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!subject.trim() || !body.trim()) return;
          send.mutate({ subject: subject.trim(), bodyText: body });
        }}
      >
        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject"
          className="w-full rounded-md border border-[var(--border)] bg-[var(--bg-card)] px-2 py-1.5 text-sm"
        />
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={6}
          placeholder="Write the reply…"
          className="w-full rounded-md border border-[var(--border)] bg-[var(--bg-card)] px-2 py-1.5 font-sans text-sm"
        />
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={!subject.trim() || !body.trim() || send.isPending}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {send.isPending ? "Sending…" : "Send reply"}
          </button>
          {send.error ? (
            <span className="text-sm text-[var(--danger)]">
              {send.error instanceof ApiError
                ? send.error.status === 429
                  ? "Daily send limit reached for this strategy. Try again later."
                  : send.error.status === 400
                  ? "Invalid reply (subject and body required, prospect must not be unsubscribed)."
                  : `Send failed (HTTP ${send.error.status})`
                : "Send failed"}
            </span>
          ) : null}
          {send.isSuccess ? <span className="text-sm text-[var(--success)]">Queued.</span> : null}
        </div>
      </form>
    </Card>
  );
}
